import { useQuery, useQueryClient } from '@tanstack/react-query';

import { execute } from '@/graphql/execute';
import { analysisRunsQuery } from '@/queries/analysisRuns.query';

import { ANALYSIS_RUN_JOB_TYPES, isRunActive } from './runs';

export const ACTIVE_RUN_POLL_MS = 2_000;
export const IDLE_RUN_POLL_MS = 30_000;
export const analysisRunsQueryKey = (projectId: string) => ['analysisRuns', projectId] as const;

/**
 * The project's analysis runs (D12), shared by the Analyses page and the
 * launcher's Recent block (same query key, one request).
 *
 * Polling is `refetchInterval`, never a manual `refetch()`: TanStack's
 * interval fetch passes no `cancelRefetch`, so a slow response is joined, not
 * cancelled and restarted (the 2026-09-22 frozen-poll incident). The payload
 * is the slim `AnalysisRun` type — no inputJson/outputJson.
 */
export function useAnalysisRuns(projectId: string | null | undefined) {
  return useQuery({
    queryKey: analysisRunsQueryKey(projectId ?? ''),
    queryFn: () => execute(analysisRunsQuery, { projectId: projectId!, types: [...ANALYSIS_RUN_JOB_TYPES] }),
    enabled: !!projectId,
    select: (data) => data.analysisRuns,
    refetchInterval: (query) => {
      const runs = query.state.data?.analysisRuns ?? [];
      const now = Date.now();
      return runs.some((r) => isRunActive(r, now)) ? ACTIVE_RUN_POLL_MS : IDLE_RUN_POLL_MS;
    },
  });
}

/**
 * Every run dialog calls this from its mutation's `onSuccess` (next to the
 * `{ toastId, batchId }` tracker registration), so the new run appears in the
 * Analyses hub immediately — and its WAITING status
 * switches the shared query to the 2 s active poll — instead of after the
 * 30 s idle poll.
 */
export function useInvalidateAnalysisRuns(): (projectId: string) => void {
  const queryClient = useQueryClient();
  return (projectId) => {
    void queryClient.invalidateQueries({ queryKey: analysisRunsQueryKey(projectId) });
  };
}
